<template>
  <div class="gallery-page">
    <!-- Hero Section with Custom Content -->
    <section class="hero-section">
      <div class="container">
        <div class="row justify-content-center text-center">
          <div class="col-lg-8">
            <div class="hero-content">
              <p class="hero-subtitle">GALLERY</p>
              <h1 class="hero-title">Videos & photos</h1>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Loading State -->
    <section v-if="loading" class="loading-section py-5">
      <div class="container text-center">
        <div class="spinner-border text-primary" style="width: 3rem; height: 3rem;" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3">Loading galleries...</p>
      </div>
    </section>

    <!-- Error State -->
    <section v-else-if="error" class="error-section py-5">
      <div class="container text-center">
        <div class="alert alert-danger">
          <h4>Error Loading Galleries</h4>
          <p>{{ error }}</p>
          <button class="btn btn-outline-danger" @click="loadGalleries">
            <i class="bi bi-arrow-clockwise me-2"></i>Retry
          </button>
        </div>
      </div>
    </section>

    <!-- Gallery Grid Section -->
    <section v-else class="gallery-section py-5">
      <div class="container ">
        <!-- Galleries Grid -->
        <div class="row g-4" v-if="galleries.length > 0">
          <div
            v-for="gallery in galleries"
            :key="gallery.id"
            class="col-lg-6 col-md-6"
          >
            <div class="gallery-item" @click="viewGallery(gallery)">
              <div class="gallery-image">
                <img
                  :src="getGalleryCoverImage(gallery)"
                  :alt="gallery.title"
                  class="img-fluid"
                  loading="lazy"
                  @error="handleImageError"
                >
                <div class="gallery-overlay">
                  <div class="gallery-content">
                    <div class="gallery-info-badge">
                      <span class="items-count">
                        <i class="bi bi-images me-1"></i>
                        {{ getItemsCount(gallery) }} items
                      </span>
                    </div>
                    <h3 class="gallery-title">{{ gallery.title }}</h3>
                    <p class="gallery-description" v-if="gallery.description">
                      {{ gallery.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-5">
          <i class="bi bi-images display-1 text-muted mb-3"></i>
          <h3>No Public Galleries Available</h3>
          <p class="text-muted">There are no public galleries to display at the moment.</p>
        </div>

        <!-- Pagination -->
        <div class="row mt-5" v-if="pagination && pagination.last_page > 1">
          <div class="col-12">
            <nav aria-label="Gallery pagination">
              <ul class="pagination justify-content-center">
                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                  <button
                    class="page-link"
                    @click="loadPage(pagination.current_page - 1)"
                    :disabled="pagination.current_page === 1"
                  >
                    Previous
                  </button>
                </li>
                <li
                  v-for="page in paginationPages"
                  :key="page"
                  class="page-item"
                  :class="{ active: page === pagination.current_page }"
                >
                  <button class="page-link" @click="loadPage(page)">{{ page }}</button>
                </li>
                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
                  <button
                    class="page-link"
                    @click="loadPage(pagination.current_page + 1)"
                    :disabled="pagination.current_page === pagination.last_page"
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <!-- Load More Button (alternative to pagination) -->
        <div class="row mt-5" v-if="!pagination && galleries.length > 0 && hasMoreItems">
          <div class="col-12 text-center">
            <button
              class="btn btn-outline-primary btn-lg"
              @click="loadMoreItems"
              :disabled="loadingMore"
            >
              <span v-if="loadingMore" class="spinner-border spinner-border-sm me-2"></span>
              {{ loadingMore ? 'Loading...' : 'Load More' }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Gallery Detail Modal -->
    <div class="modal fade" id="galleryModal" tabindex="-1" ref="galleryModal" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <div>
              <h4 class="modal-title">{{ selectedGallery?.title }}</h4>
              <small class="text-muted" style="color: #fff !important;">
                {{ getItemsCount(selectedGallery) }} items
                • Created {{ formatDate(selectedGallery?.created_at) }}
              </small>
              <div v-if="selectedGallery?.description" style="padding-top: 1rem;">
                <p>{{ selectedGallery.description }}</p>
              </div>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">


            <!-- Gallery Items Grid -->
            <div class="row g-3" v-if="hasGalleryItems(selectedGallery)">
              <div
                v-for="item in getGalleryItems(selectedGallery)"
                :key="item.id"
                class="col-md-4 col-sm-6"
              >
                <div class="gallery-item-card" @click="openMediaModal(item)">
                  <div class="item-image">
                    <img
                      :src="getItemThumbnail(item)"
                      :alt="item.title"
                      class="img-fluid"
                      loading="lazy"
                      @error="handleImageError"
                    >
                    <div class="item-overlay">
                      <div class="play-button" v-if="item.type === 'video'">
                        <i class="bi bi-play-circle"></i>
                      </div>
                      <div class="view-button" v-else>
                        <i class="bi bi-eye"></i>
                      </div>
                    </div>
                    <div class="item-type-badge">
                      <span class="badge" :class="item.type === 'video' ? 'bg-danger' : 'bg-success'">
                        {{ item.type }}
                      </span>
                    </div>
                  </div>
                  <div class="item-info">
                    <h6 class="item-title">{{ item.title }}</h6>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty Gallery -->
            <div v-else class="text-center py-4">
              <i class="bi bi-images display-4 text-muted mb-3"></i>
              <p class="text-muted">This gallery is empty.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Media Modal -->
    <div class="modal fade" id="mediaModal" tabindex="-1" ref="mediaModal" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ selectedMedia?.title }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body p-0">
            <!-- Video Content -->
            <div v-if="showMediaPlayer && selectedMedia?.type === 'video'" class="video-container">
            <!-- YouTube Video -->
              <div v-if="selectedMedia?.youtube_url" class="youtube-container">
                <iframe
                  :src="getYouTubeEmbedUrl(selectedMedia.youtube_url)"
                  frameborder="0"
                  allowfullscreen
                  ref="youtubePlayer"
                  class="w-100"
                  style="height: 500px;"
                ></iframe>
              </div>
              <!-- Vimeo Video -->
              <div v-else-if="selectedMedia?.vimeo_url" class="vimeo-container">
                <iframe
                  :src="getVimeoEmbedUrl(selectedMedia.vimeo_url)"
                  frameborder="0"
                  ref="vimeoPlayer"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowfullscreen
                ></iframe>
              </div>
              <!-- Regular Video File -->
              <video
                v-else
                ref="videoPlayer"
                class="w-100"
                controls
                :poster="getItemThumbnail(selectedMedia)"
                style="max-height: 500px;"
              >
                <source :src="getItemFileUrl(selectedMedia)" type="video/mp4">
                Your browser does not support the video tag.
              </video>
            </div>
            <!-- Image Content -->
            <div v-else class="image-container">
              <img
                :src="getItemFileUrl(selectedMedia)"
                :alt="selectedMedia?.title"
                class="img-fluid w-100"
                style="max-height: 500px; object-fit: contain;"
                @error="handleImageError"
              >
            </div>
          </div>
          <div class="modal-footer" v-if="selectedMedia?.description">
            <p class="modal-description text-muted mb-0">{{ selectedMedia.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GalleryApiService from '@/services/gallery/GalleryApiService'

export default {
  name: 'PublicGallery',

  data() {
    return {
      galleries: [],
      selectedGallery: null,
      selectedMedia: null,
      searchQuery: '',
      sortBy: 'created_at',
      pagination: null,
      loading: true,
      loadingMore: false,
      error: null,
      searchTimeout: null,
      hasMoreItems: true,
      currentPage: 1,
      showMediaPlayer: false,
      // Use a proper placeholder that won't cause 404s
      placeholderImage: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZTllY2VmIi8+CiAgPHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzZjNzU3ZCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K'
    }
  },

  computed: {
    paginationPages() {
      if (!this.pagination) return []

      const pages = []
      const current = this.pagination.current_page
      const last = this.pagination.last_page

      // Show up to 5 pages around current page
      for (let i = Math.max(1, current - 2); i <= Math.min(last, current + 2); i++) {
        pages.push(i)
      }

      return pages
    }
  },

  mounted() {
    this.loadGalleries();

    const mediaModal = this.$refs.mediaModal;

    if (mediaModal) {
      mediaModal.addEventListener("hidden.bs.modal", () => {
        this.stopMediaPlayback();
      });
    }
  },

  methods: {
    stopMediaPlayback() {
      // Remove the iframe/video element entirely → GUARANTEED to stop playback
      this.showMediaPlayer = false;

      // Also reset selected media
      this.selectedMedia = null;
    },

    async loadGalleries(page = 1) {
      try {
        this.loading = true
        this.error = null

        const params = {
          page: page,
          per_page: 12,
          include_private: 'false'
        }

        if (this.searchQuery) params.search = this.searchQuery
        if (this.sortBy) params.sort = this.sortBy

        const response = await GalleryApiService.getPublicGalleries(params)

        if (response && response.data) {
          this.galleries = Array.isArray(response.data) ? response.data : []
          this.pagination = response.pagination || null
          this.currentPage = page
        } else {
          this.galleries = []
          this.pagination = null
        }

      } catch (error) {
        console.error('Error loading public galleries:', error)
        this.error = error.message || 'Failed to load galleries'
        this.galleries = []
      } finally {
        this.loading = false
      }
    },

    async loadMoreItems() {
      if (this.pagination && this.currentPage < this.pagination.last_page) {
        this.loadingMore = true
        const nextPage = this.currentPage + 1

        try {
          const params = {
            page: nextPage,
            per_page: 12,
            include_private: 'false'
          }

          if (this.searchQuery) {
            params.search = this.searchQuery
          }
          if (this.sortBy) {
            params.sort = this.sortBy
          }

          const response = await GalleryApiService.getPublicGalleries(params)

          if (response && response.data) {
            const newGalleries = Array.isArray(response.data) ? response.data : []
            this.galleries.push(...newGalleries)
            this.currentPage = nextPage
            this.hasMoreItems = nextPage < (response.pagination?.last_page || 1)
          }
        } catch (error) {
          console.error('Error loading more galleries:', error)
        } finally {
          this.loadingMore = false
        }
      } else {
        this.hasMoreItems = false
      }
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadGalleries()
      }, 500)
    },

    loadPage(page) {
      if (page >= 1 && page <= (this.pagination?.last_page || 1)) {
        this.loadGalleries(page)
      }
    },

    async viewGallery(gallery) {
      try {
        const response = await GalleryApiService.getGallery(gallery.id)
        this.selectedGallery = response

        const modalElement = this.$refs.galleryModal
        if (modalElement) {
          if (window.bootstrap && window.bootstrap.Modal) {
            // Reuse a single instance so options stick
            if (!this.galleryModalInstance) {
              this.galleryModalInstance = new window.bootstrap.Modal(modalElement, {
                backdrop: 'static',
                keyboard: false
              })
            }
            this.galleryModalInstance.show()
          } else if (window.$ && window.$.fn.modal) {
            window.$(modalElement).modal({
              backdrop: 'static',
              keyboard: false
            })
          } else {
            // Fallback (this one won't close on outside click anyway)
            modalElement.style.display = 'block'
            modalElement.classList.add('show')
            document.body.classList.add('modal-open')

            const backdrop = document.createElement('div')
            backdrop.className = 'modal-backdrop fade show'
            document.body.appendChild(backdrop)

            const closeBtn = modalElement.querySelector('.btn-close')
            if (closeBtn) {
              closeBtn.onclick = () => this.closeModal(modalElement, backdrop)
            }
          }
        }
      } catch (error) {
        console.error('Error loading gallery details:', error)
        alert('Error loading gallery details: ' + error.message)
      }
    },

    getVimeoEmbedUrl(url) {
      if (!url) return ''

      // Extract only digits from Vimeo URL
      const match = url.match(/vimeo\.com\/(?:video\/|channels\/[^\/]+\/)?(\d+)/)

      if (match && match[1]) {
        return `https://player.vimeo.com/video/${match[1]}`
      }

      return url
    },

    closeModal(modalElement, backdrop) {
      modalElement.style.display = 'none'
      modalElement.classList.remove('show')
      document.body.classList.remove('modal-open')
      if (backdrop) {
        backdrop.remove()
      }
    },

    getYouTubeThumbnail(url) {
      if (!url) return null;

      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);

      if (match && match[2].length === 11) {
        return `https://img.youtube.com/vi/${match[2]}/maxresdefault.jpg`;
      }

      return null;
    },

    openMediaModal(item) {
      this.selectedMedia = item
      this.showMediaPlayer = true;

      const modalElement = this.$refs.mediaModal
      if (modalElement) {
        if (window.bootstrap && window.bootstrap.Modal) {
          if (!this.mediaModalInstance) {
            this.mediaModalInstance = new window.bootstrap.Modal(modalElement, {
              backdrop: 'static',
              keyboard: false
            })
          }
          this.mediaModalInstance.show()
        } else if (window.$ && window.$.fn.modal) {
          window.$(modalElement).modal({
            backdrop: 'static',
            keyboard: false
          })
        } else {
          modalElement.style.display = 'block'
          modalElement.classList.add('show')
          document.body.classList.add('modal-open')

          const backdrop = document.createElement('div')
          backdrop.className = 'modal-backdrop fade show'
          document.body.appendChild(backdrop)

          const closeBtn = modalElement.querySelector('.btn-close')
          if (closeBtn) {
            closeBtn.onclick = () => this.closeModal(modalElement, backdrop)
          }
        }
      }
    },

    getGalleryCoverImage(gallery) {
      if (!gallery) return this.placeholderImage;

      // 1. Dedicated cover image set by admin (highest priority)
      if (gallery.cover_image_url) {
        return gallery.cover_image_url;
      }

      if (gallery.cover_image) {
        return gallery.cover_image;
      }

      // 2. If gallery already has its own thumbnail (backend should ideally set this)
      if (gallery.thumbnail_url) {
        return gallery.thumbnail_url;
      }

      // 3. If backend exposes first item metadata (most APIs do)
      if (gallery.first_item_thumbnail) {
        return gallery.first_item_thumbnail;
      }

      if (gallery.first_item && gallery.first_item.thumbnail_url) {
        return gallery.first_item.thumbnail_url;
      }

      // 4. Fallback for YouTube links if backend exposes URL but not thumbnail
      if (gallery.youtube_url) {
        const ytThumb = this.getYouTubeThumbnail(gallery.youtube_url);
        if (ytThumb) return ytThumb;
      }

      // 4. If the gallery *does* have items (only after modal load)
      if (gallery.items && gallery.items.length > 0) {
        const first = gallery.items[0];

        if (first.thumbnail_url) return first.thumbnail_url;

        if (first.type === "video" && first.youtube_url) {
          return this.getYouTubeThumbnail(first.youtube_url);
        }
      }

      return this.placeholderImage;
    },

    getItemsCount(gallery) {
      if (!gallery) return 0
      return gallery.items_count || (gallery.items ? gallery.items.length : 0) || 0
    },

    hasGalleryItems(gallery) {
      if (!gallery) return false
      return gallery.items && Array.isArray(gallery.items) && gallery.items.length > 0
    },

    getGalleryItems(gallery) {
      if (!gallery || !gallery.items) return []
      return Array.isArray(gallery.items) ? gallery.items : []
    },

    getItemThumbnail(item) {
      if (!item) return this.placeholderImage;

      // Always trust provided thumbnail first
      if (item.thumbnail_url && !item.thumbnail_url.includes('/api/placeholder/')) {
        return item.thumbnail_url;
      }

      // YouTube fallback if needed
      if (item.type === 'video' && item.youtube_url) {
        const yt = this.getYouTubeThumbnail(item.youtube_url);
        if (yt) return yt;
      }

      // fallback to file URL
      if (item.file_url && !item.file_url.includes('/api/placeholder/')) {
        return item.file_url;
      }

      return this.placeholderImage;
    },

    getItemFileUrl(item) {
      if (!item) return this.placeholderImage

      const fileUrl = item.file_url || item.thumbnail_url
      if (fileUrl && !fileUrl.includes('/api/placeholder/')) {
        return fileUrl
      }

      return this.placeholderImage
    },

    getYouTubeEmbedUrl(url) {
      if (!url) return ''

      // Convert YouTube URL to embed URL
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
      const match = url.match(regExp)

      if (match && match[2].length === 11) {
        return `https://www.youtube.com/embed/${match[2]}`
      }

      return url
    },

    handleImageError(event) {
      console.warn('Image failed to load:', event.target.src)

      // Only set placeholder if we haven't already, to avoid loops
      if (!event.target.src.startsWith('data:image/svg+xml')) {
        event.target.src = this.placeholderImage
        // Mark as failed to prevent retries
        event.target.dataset.failed = 'true'
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return GalleryApiService.formatDate(dateString)
    }
  },

  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // Clean up video when component is destroyed
    if (this.$refs.videoPlayer) {
      this.$refs.videoPlayer.pause()
    }
  }
}
</script>

<style scoped>
/* Hero Section - Updated without hardcoded text */
.hero-section {
  color: white;
  padding: 6rem 0 4rem;
  text-align: center;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  line-height: 1.2;
}

.hero-description {
  font-size: 1.2rem;
  opacity: 0.9;
  margin: 0;
}

/* Search and Filter */
.search-box input {
  border-radius: 8px;
  border: 2px solid #e9ecef;
  padding: 0.75rem;
}

.search-box input:focus {
  border-color: #1a8090;
  box-shadow: 0 0 0 0.2rem rgba(26, 128, 144, 0.25);
}

/* Gallery Section */
.gallery-section {
  background-color: #f8f9fa;
  padding: 6rem 0;
}

.gallery-item {
  position: relative;
  overflow: hidden;
  border-radius: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 350px;
}

.gallery-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(26, 128, 144, 0.2);
}

.gallery-image {
  position: relative;
  height: 100%;
  overflow: hidden;
  border-radius: 15px;
}

.gallery-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.gallery-item:hover .gallery-image img {
  transform: scale(1.05);
}

.gallery-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(26, 128, 144, 0.8) 0%, rgba(13, 93, 107, 0.9) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  padding: 2rem;
}

.gallery-item:hover .gallery-overlay {
  opacity: 1;
}

.gallery-content {
  text-align: center;
  color: white;
}

.gallery-info-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  display: inline-block;
  margin-bottom: 1rem;
  backdrop-filter: blur(10px);
}

.items-count {
  font-size: 0.9rem;
  font-weight: 500;
}

.gallery-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.gallery-description {
  font-size: 0.95rem;
  opacity: 0.9;
  margin-bottom: 1.5rem;
  line-height: 1.4;
}

.view-button {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  display: inline-block;
  font-weight: 600;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.view-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* Gallery Items in Modal */
.gallery-item-card {
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.gallery-item-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.item-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.gallery-item-card:hover .item-image img {
  transform: scale(1.1);
}

.item-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(26, 128, 144, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.gallery-item-card:hover .item-overlay {
  opacity: 1;
}

.play-button,
.view-button {
  color: white;
  font-size: 2rem;
}

.item-type-badge {
  position: absolute;
  top: 10px;
  right: 10px;
}

.item-info {
  padding: 1rem;
}

.item-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

/* Modal Styles */
.modal-content {
  border: none;
  border-radius: 15px;
  overflow: hidden;
}

.modal-header {
  background-color: #1a8090;
  color: white;
  border-bottom: none;
}

.modal-title {
  font-weight: 700;
  font-size: 1.8rem;
}

.btn-close {
  filter: brightness(0) invert(1);
}

.video-container,
.image-container {
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.youtube-container {
  width: 100%;
  height: 500px;
}

.modal-footer {
  background-color: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

.modal-description {
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Button Styles */
.btn-primary {
  background-color: #009CAD;
  border-color: #009CAD;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  background-color: #0d5d6b;
  border-color: #0d5d6b;
  transform: translateY(-1px);
}

.btn-outline-primary {
  color: #009CAD;
  border-color: #009CAD;
  padding: 0.75rem 2rem;
  font-weight: 600;
  border-width: 2px;
  transition: all 0.3s ease;
}

.btn-outline-primary:hover {
  background-color: #009CAD;
  border-color: #009CAD;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(26, 128, 144, 0.3);
}

.btn-outline-danger {
  color: #dc3545;
  border-color: #dc3545;
}

.btn-outline-danger:hover {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
}

/* Pagination */
.pagination .page-link {
  color: #1a8090;
  border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
  background-color: #1a8090;
  border-color: #1a8090;
}

.pagination .page-link:hover {
  color: #0d5d6b;
  background-color: #e9ecef;
  border-color: #dee2e6;
}

/* Loading States */
.loading-section {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.error-section {
  min-height: 400px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .gallery-item {
    height: 280px;
  }

  .gallery-title {
    font-size: 1.3rem;
  }

  .gallery-content {
    padding: 1.5rem;
  }

  .hero-section {
    padding: 5rem 0 3rem;
  }

  .gallery-section {
    padding: 4rem 0;
  }

  .item-image {
    height: 150px;
  }
}

@media (max-width: 576px) {
  .hero-title {
    font-size: 2rem;
  }

  .gallery-item {
    height: 250px;
  }

  .gallery-title {
    font-size: 1.1rem;
  }

  .modal-dialog {
    margin: 0.5rem;
  }

  .modal-title {
    font-size: 1.1rem;
  }

  .youtube-container {
    height: 300px;
  }
}

/* Animation */
.gallery-item {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Empty state */
.text-center i.display-1 {
  color: #dee2e6;
}

.vimeo-container {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 ratio */
  background: #000;
}

.vimeo-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

</style>
